import React, { useState, useEffect, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { compararArticulos, getArticulosGlobal } from "../api"
import {
  formatCurrency, formatNumber, formatCantidad, formatPercent, claseABC, colorPara,
} from "../helpers"

const MAX_PRODUCTOS = 5

const Pastilla = ({ producto, onQuitar }) => {
  const cfg = claseABC(producto.abc?.clase)
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: "#fff", border: "1px solid #e2e8f0",
      borderLeft: `3px solid ${cfg.chartColor}`,
      borderRadius: 6, padding: "4px 6px 4px 10px",
      fontSize: 12, color: "#0f172a", fontWeight: 600, maxWidth: 280,
    }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
        {producto.descripcion || producto.id_producto}
      </span>
      <button type="button" onClick={() => onQuitar(producto.id_producto)}
        style={{ background: "none", border: "none", padding: 2, color: "#94a3b8", cursor: "pointer" }}
        title="Quitar"
      >
        <i className="mdi mdi-close" style={{ fontSize: 14 }} />
      </button>
    </span>
  )
}

Pastilla.propTypes = { producto: PropTypes.object, onQuitar: PropTypes.func }

const Selector = ({ filtros, productosSel, onAgregar }) => {
  const [busqueda, setBusqueda] = useState("")
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)
  const [debounced, setDebounced] = useState("")

  useEffect(() => {
    const t = setTimeout(() => setDebounced(busqueda.trim()), 350)
    return () => clearTimeout(t)
  }, [busqueda])

  useEffect(() => {
    if (!debounced || !filtros?.nitemisor) { setResultados([]); return }
    let cancelled = false
    setBuscando(true)
    // Pedimos top 200 ordenado por importe; filtramos en cliente por descripción/id
    getArticulosGlobal({ ...filtros, order_by: "importe_total", order_dir: "desc", limit: 200, offset: 0 })
      .then(res => {
        if (cancelled) return
        const lista = (res.data?.data || res.data?.articulos || [])
        const q = debounced.toLowerCase()
        setResultados(lista.filter(p =>
          String(p.descripcion || "").toLowerCase().includes(q) ||
          String(p.id_producto || "").toLowerCase().includes(q),
        ).slice(0, 30))
      })
      .catch(() => { if (!cancelled) setResultados([]) })
      .finally(() => { if (!cancelled) setBuscando(false) })
    return () => { cancelled = true }
  }, [debounced, filtros])

  const idsSel = new Set(productosSel.map(p => p.id_producto))
  const limiteAlcanzado = productosSel.length >= MAX_PRODUCTOS

  return (
    <div>
      <div style={{ position: "relative", maxWidth: 480 }}>
        <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={limiteAlcanzado ? `Máximo ${MAX_PRODUCTOS} productos` : "Buscar producto por nombre o ID…"}
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          disabled={limiteAlcanzado}
          style={{ paddingLeft: 32 }}
        />
      </div>
      {debounced && !limiteAlcanzado && (
        <div style={{
          marginTop: 6, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
          maxHeight: 280, overflowY: "auto", maxWidth: 480,
        }}>
          {buscando && (
            <div style={{ padding: 12, textAlign: "center", color: "#64748b", fontSize: 13 }}>
              <Spinner size="sm" /> Buscando…
            </div>
          )}
          {!buscando && resultados.length === 0 && (
            <div style={{ padding: 12, textAlign: "center", color: "#64748b", fontSize: 13 }}>
              Sin resultados.
            </div>
          )}
          {!buscando && resultados.map(p => {
            const ya = idsSel.has(p.id_producto)
            const cfg = claseABC(p.abc?.clase)
            return (
              <button key={p.id_producto}
                type="button"
                disabled={ya}
                onClick={() => onAgregar(p)}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  width: "100%", padding: "8px 12px",
                  background: ya ? "#f1f5f9" : "#fff",
                  border: "none", borderBottom: "1px solid #f1f5f9",
                  cursor: ya ? "not-allowed" : "pointer", fontSize: 13, textAlign: "left",
                  opacity: ya ? 0.5 : 1,
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.descripcion} <span style={{ color: "#94a3b8", fontSize: 11 }}>· {p.id_producto}</span>
                </span>
                <span style={{
                  background: cfg.bg, color: cfg.color,
                  padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, marginLeft: 8,
                }}>
                  {cfg.label}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

Selector.propTypes = { filtros: PropTypes.object, productosSel: PropTypes.array, onAgregar: PropTypes.func }

// Definición de métricas a comparar (label + extractor + dirección "alto"=mejor o "bajo"=mejor)
const METRICAS = [
  { key: "importe_neto", label: "Importe neto", get: p => p.financiero?.importe_neto, fmt: formatCurrency, mejor: "alto" },
  { key: "cantidad_neta", label: "Unidades vendidas", get: p => p.cantidades?.neta, fmt: v => formatCantidad(v), mejor: "alto" },
  { key: "facturas", label: "Facturas", get: p => p.distribucion?.facturas_count, fmt: v => formatNumber(v), mejor: "alto" },
  { key: "clientes", label: "Clientes únicos", get: p => p.distribucion?.clientes_unicos, fmt: v => formatNumber(v), mejor: "alto" },
  { key: "sucursales", label: "Sucursales con venta", get: p => p.distribucion?.sucursales_vendido, fmt: v => formatNumber(v), mejor: "alto" },
  { key: "penetracion", label: "Penetración sucursales %", get: p => p.distribucion?.penetracion_sucursales_pct, fmt: v => formatPercent(v), mejor: "alto" },
  { key: "ticket_prom", label: "Ticket promedio", get: p => p.financiero?.ticket_promedio_factura, fmt: formatCurrency, mejor: "alto" },
  { key: "precio_prom", label: "Precio promedio", get: p => p.precios?.promedio, fmt: formatCurrency, mejor: null },
  { key: "cv_pct", label: "Coef. variación precio %", get: p => p.precios?.coef_variacion_pct, fmt: v => formatPercent(v), mejor: "bajo" },
  { key: "dias_ventas", label: "Días con ventas", get: p => p.movilidad?.dias_con_ventas, fmt: v => formatNumber(v), mejor: "alto" },
  { key: "penetracion_temp", label: "Penetración temporal %", get: p => p.movilidad?.penetracion_temporal_pct, fmt: v => formatPercent(v), mejor: "alto" },
]

const ComparadorTab = ({ filtros, seleccionadosIniciales, onPrecargado }) => {
  const [seleccionados, setSeleccionados] = useState([]) // array de productos ligeros (de la búsqueda)
  const [comparativa, setComparativa] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // Precargar selección si vinieron desde otro contexto (ej: drawer "Comparar con co-purchase")
  useEffect(() => {
    if (Array.isArray(seleccionadosIniciales) && seleccionadosIniciales.length > 0) {
      setSeleccionados(seleccionadosIniciales.slice(0, MAX_PRODUCTOS))
      onPrecargado?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionadosIniciales])

  const agregar = useCallback((p) => {
    if (seleccionados.length >= MAX_PRODUCTOS) return
    if (seleccionados.some(s => s.id_producto === p.id_producto)) return
    setSeleccionados([...seleccionados, p])
  }, [seleccionados])

  const quitar = useCallback((id) => {
    setSeleccionados(prev => prev.filter(p => p.id_producto !== id))
  }, [])

  const limpiar = () => { setSeleccionados([]); setComparativa(null) }

  const consultarComparativa = useCallback(async () => {
    if (seleccionados.length < 2) return
    setLoading(true)
    setError("")
    try {
      const { data } = await compararArticulos({
        ...filtros,
        ids: seleccionados.map(p => p.id_producto),
      })
      setComparativa(data)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar la comparativa.")
    } finally {
      setLoading(false)
    }
  }, [seleccionados, filtros])

  // Auto-consultar al cambiar selección (debounced)
  useEffect(() => {
    if (seleccionados.length < 2) { setComparativa(null); return }
    const t = setTimeout(() => { consultarComparativa() }, 400)
    return () => clearTimeout(t)
  }, [seleccionados, consultarComparativa])

  // Si la API trae detalles enriquecidos por producto los usamos; si no, lo de la búsqueda.
  // Aplanamos el shape: el back trae las dimensiones dentro de `resumen`, y la lista las
  // trae directo. Mezclamos para que los `m.get(p)` funcionen igual.
  const productosFinal = useMemo(() => {
    const lista = comparativa?.productos || comparativa?.data || seleccionados
    return lista.map(p => ({ ...p, ...(p.resumen || {}) }))
  }, [comparativa, seleccionados])

  // Calcular mejor/peor por métrica
  const calculoMejorPeor = useMemo(() => {
    const out = {}
    METRICAS.forEach(m => {
      if (!m.mejor) return
      const valores = productosFinal.map(p => Number(m.get(p))).filter(v => isFinite(v))
      if (valores.length < 2) return
      const min = Math.min(...valores)
      const max = Math.max(...valores)
      out[m.key] = m.mejor === "alto" ? { mejor: max, peor: min } : { mejor: min, peor: max }
    })
    return out
  }, [productosFinal])

  if (seleccionados.length === 0) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 30, border: "1px solid #e2e8f0" }}>
        <div style={{ textAlign: "center", color: "#64748b", marginBottom: 20 }}>
          <i className="mdi mdi-compare-horizontal" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Comparador de productos
          </h5>
          <p style={{ fontSize: 14, maxWidth: 460, margin: "8px auto 0" }}>
            Selecciona entre 2 y {MAX_PRODUCTOS} productos para verlos lado a lado y descubrir cuál
            performa mejor en cada métrica clave.
          </p>
        </div>
        <Selector filtros={filtros} productosSel={seleccionados} onAgregar={agregar} />
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Selector + pastillas */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-compare-horizontal me-2 text-primary" />
            Productos seleccionados ({seleccionados.length}/{MAX_PRODUCTOS})
          </h6>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={limpiar}>
            <i className="mdi mdi-close-circle-outline me-1" /> Limpiar todo
          </button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {seleccionados.map(p => (
            <Pastilla key={p.id_producto} producto={p} onQuitar={quitar} />
          ))}
        </div>
        {seleccionados.length < MAX_PRODUCTOS && (
          <Selector filtros={filtros} productosSel={seleccionados} onAgregar={agregar} />
        )}
      </div>

      {/* Tabla comparativa */}
      {seleccionados.length < 2 ? (
        <div style={{ background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e", padding: "12px 16px", borderRadius: 10, fontSize: 13 }}>
          <i className="mdi mdi-information-outline me-1" />
          Agrega al menos 2 productos para comparar.
        </div>
      ) : loading ? (
        <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" /> <div className="mt-2">Calculando comparativa…</div>
        </div>
      ) : error ? (
        <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 8 }}>
          <i className="mdi mdi-alert-circle me-1" />{error}
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  <th style={{
                    padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600,
                    color: "#475569", textTransform: "uppercase", letterSpacing: 0.4,
                    minWidth: 200,
                  }}>Métrica</th>
                  {productosFinal.map((p, i) => (
                    <th key={p.id_producto} style={{
                      padding: "10px 12px", textAlign: "right",
                      fontSize: 12, color: "#0f172a", fontWeight: 700, minWidth: 180,
                      borderLeft: `3px solid ${colorPara(i)}`,
                    }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.descripcion}
                      </div>
                      <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontWeight: 400 }}>
                        ID: {p.id_producto}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICAS.map(m => (
                  <tr key={m.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 12px", color: "#475569", fontWeight: 500 }}>{m.label}</td>
                    {productosFinal.map(p => {
                      const val = m.get(p)
                      const num = Number(val)
                      const mp = calculoMejorPeor[m.key]
                      const esMejor = mp && isFinite(num) && num === mp.mejor
                      const esPeor = mp && isFinite(num) && num === mp.peor && mp.mejor !== mp.peor
                      return (
                        <td key={p.id_producto} style={{
                          padding: "10px 12px", textAlign: "right",
                          fontWeight: esMejor || esPeor ? 700 : 500,
                          color: esMejor ? "#10b981" : esPeor ? "#ef4444" : "#0f172a",
                          background: esMejor ? "#d1fae5" : esPeor ? "#fee2e2" : undefined,
                        }}>
                          {m.fmt(val)}
                          {esMejor && <i className="mdi mdi-trophy ms-1" title="Mejor" />}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

ComparadorTab.propTypes = {
  filtros: PropTypes.object,
  seleccionadosIniciales: PropTypes.array,
  onPrecargado: PropTypes.func,
}

export default ComparadorTab
